// In src/pages/InquiriesPage.js

import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
// ***** MODIFIED: Import collectionGroup *****
import { collection, query, orderBy, onSnapshot, doc, updateDoc, collectionGroup } from 'firebase/firestore'; 
import { Container, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Select, MenuItem, FormControl, Alert, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { format } from 'date-fns';

function InquiriesPage() {
  const { userProfile } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [allChurches, setAllChurches] = useState([]);
  const [loading, setLoading] = useState(true);

  const canView = userProfile?.role === 'Church Administrator' || 
                  userProfile?.role === 'Super Administrator' || 
                  userProfile?.canViewInquiries === true;

  const isSuperAdmin = userProfile?.role === 'Super Administrator';

  // Fetch all churches if the user is a Super Admin
  useEffect(() => {
    if (isSuperAdmin) {
      const churchesQuery = query(collection(db, 'churches'), orderBy('name', 'asc'));
      const unsubscribe = onSnapshot(churchesQuery, (snapshot) => {
        setAllChurches(snapshot.docs.map(doc => ({ id: doc.id, name: doc.data().name })));
      });
      return () => unsubscribe();
    }
  }, [isSuperAdmin]);
  
  // Fetch inquiries
  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    
    let inquiriesQuery;
    if (isSuperAdmin) {
      // ***** CORRECTED: Use collectionGroup for Super Admin query *****
      inquiriesQuery = query(collectionGroup(db, 'inquiries'), orderBy('createdAt', 'desc'));
    } else {
      if (!userProfile?.churchId) {
        setLoading(false);
        return;
      }
      const inquiriesRef = collection(db, 'churches', userProfile.churchId, 'inquiries');
      inquiriesQuery = query(inquiriesRef, orderBy('createdAt', 'desc'));
    }

    const unsubscribe = onSnapshot(inquiriesQuery, (snapshot) => {
      // For super admin, we get documents from a collection group, which don't have the churchId directly.
      // We need to get it from the document path.
      const inquiriesData = snapshot.docs.map(doc => {
        const data = doc.data();
        const churchId = isSuperAdmin ? doc.ref.parent.parent.id : userProfile.churchId;
        return { id: doc.id, churchId, ...data };
      });
      setInquiries(inquiriesData);
      setLoading(false);
    }, (error) => {
        console.error("Error fetching inquiries: ", error);
        setLoading(false);
    });

    return () => unsubscribe();
  }, [userProfile, canView, isSuperAdmin]);

  const handleStatusChange = async (inquiryId, newStatus, churchId) => {
    const inquiryRef = doc(db, 'churches', churchId, 'inquiries', inquiryId);
    await updateDoc(inquiryRef, { status: newStatus });
  };

  const statusColors = {
    new: 'error.main',
    in_progress: 'warning.main',
    resolved: 'success.main',
  };

  // Group inquiries by church for Super Admin view
  const groupedInquiries = useMemo(() => {
    if (!isSuperAdmin) return null;
    const groups = {};
    inquiries.forEach(item => {
      const church = allChurches.find(c => c.id === item.churchId);
      const churchName = church ? church.name : 'Unknown Church';
      if (!groups[churchName]) groups[churchName] = [];
      groups[churchName].push(item);
    });
    return groups;
  }, [inquiries, allChurches, isSuperAdmin]);


  if (loading) return <CircularProgress />;

  if (!canView) {
      return (
          <Container>
              <Alert severity="error">You do not have permission to view this page.</Alert>
          </Container>
      )
  }

  // Common component for rendering a table of inquiries
  const renderInquiriesTable = (inquiryList) => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>From</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Message</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {inquiryList.map((item) => (
            <TableRow key={item.id}>
              <TableCell>{item.createdAt ? format(item.createdAt.toDate(), 'P p') : 'N/A'}</TableCell>
              <TableCell>{item.submittedByName}<br/>({item.submittedByEmail})</TableCell>
              <TableCell>{item.type}</TableCell>
              <TableCell sx={{whiteSpace: 'pre-wrap', minWidth: 300}}>{item.message}</TableCell>
              <TableCell>
                <FormControl size="small">
                  <Select
                    value={item.status}
                    onChange={(e) => handleStatusChange(item.id, e.target.value, item.churchId)}
                    sx={{
                      color: statusColors[item.status],
                      fontWeight: 'bold',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: statusColors[item.status] },
                    }}
                  >
                    <MenuItem value="new">New</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom>Member Inquiries</Typography>
      
      {isSuperAdmin ? (
        // Super Admin View with Accordions
        Object.keys(groupedInquiries).sort().map(churchName => (
          <Accordion key={churchName} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">{churchName} ({groupedInquiries[churchName].length})</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {renderInquiriesTable(groupedInquiries[churchName])}
            </AccordionDetails>
          </Accordion>
        ))
      ) : (
        // Regular Admin View with a single table
        renderInquiriesTable(inquiries)
      )}
    </Container>
  );
}

export default InquiriesPage;